import { XMarkIcon } from "@heroicons/react/20/solid";
import { ModalConfig, useModalStore } from "@/stores/use-modal-store";
import { cn } from "@/utils/classes";

// Matches the colours used for these metrics in VideoAttentionGraph and the video benchmark chart.
const METRIC_COLORS = {
  attention: "#19D3C8",
  hook: "#F2318A",
  visualEase: "#9CA3AF",
  hold: "#9747FF",
};

const QUALITY_SCORE_ROWS = [
  {
    key: "hook",
    label: "Hook",
    description: "Securing viewer attention in the opening moments. Only measured in scenes within first 3 seconds.",
    outcome: "VTR",
  },
  {
    key: "visualEase",
    label: "Visual ease",
    description: "Smooth, coherent guidance of attention through the video.",
    outcome: "Drop off",
  },
  {
    key: "hold",
    label: "Hold",
    description: "Sustaining viewer attention across the full duration. Not measured for opening scenes (first 3 seconds).",
    outcome: "Drop off",
  },
] as const;

// The video in-depth view is always rendered on a fixed dark background regardless of the app
// theme, so this modal matches that instead of the theme-aware daisyUI modal-box.
export const VIDEO_METRIC_DEFINITIONS_MODAL_CONFIG: ModalConfig = { className: "w-full max-w-[770px]" };

/**
 * VideoMetricDefinitionsModal component
 *
 * Reference table explaining the video Attention pillar and its Hook/Visual ease/Hold quality
 * scores, plus the business outcome each quality score is used to predict.
 */
export const VideoMetricDefinitionsModal = () => {
  /* Hooks */
  const { close } = useModalStore((state) => state.modalActions);

  /* Render */
  return (
    <div className="flex flex-col gap-4 -m-6 p-6 rounded-2xl bg-[#1d232a]">
      {/* Header */}
      <div className="flex items-center gap-2.5">
        <h2 className="flex-1 text-white font-semibold text-lg">Metric definitions</h2>
        <button type="button" onClick={close} aria-label="Close" className="cursor-pointer">
          <XMarkIcon className="size-5 text-white" />
        </button>
      </div>

      <div className="flex flex-col rounded-lg overflow-hidden">
        {/* Pillar header */}
        <div className="flex bg-[#323c49]">
          <div className="flex-1 px-4 py-3">
            <span className="text-white text-sm font-semibold">Pillar</span>
          </div>
          <div className="w-[177px] shrink-0" />
        </div>

        {/* Attention row */}
        <div className="flex items-center gap-4 px-4 py-4">
          <div className="flex items-center gap-1.5 w-36 shrink-0">
            <span className="size-4 rounded-sm shrink-0" style={{ backgroundColor: METRIC_COLORS.attention }} />
            <span className="text-white font-semibold">Attention</span>
          </div>
          <p className="flex-1 text-white/70 text-sm">
            The viewer&apos;s attentional experience is efficient, coherent, and sustained. It is made up of three supporting metrics: Hook, Visual ease and
            Hold.
          </p>
          <div className="w-[177px] shrink-0" />
        </div>

        {/* Quality scores / Business outcomes header */}
        <div className="flex bg-[#323c49]">
          <div className="flex-1 px-4 py-3">
            <span className="text-white text-sm font-semibold">Quality scores</span>
          </div>
          <div className="w-[177px] shrink-0 px-4 py-3">
            <span className="text-white text-sm font-semibold">Business outcomes</span>
          </div>
        </div>

        {QUALITY_SCORE_ROWS.map((row, index) => (
          <div key={row.key} className={cn("flex items-center gap-4 px-4 py-3", index < QUALITY_SCORE_ROWS.length - 1 && "border-b border-[#323c49]")}>
            <div className="flex items-center gap-1.5 w-36 shrink-0">
              <span className="size-4 rounded-sm shrink-0" style={{ backgroundColor: METRIC_COLORS[row.key] }} />
              <span className="text-white text-sm font-semibold">{row.label}</span>
            </div>
            <p className="flex-1 text-white/70 text-sm">{row.description}</p>
            <div className="w-[177px] shrink-0">
              <span className="inline-flex items-center rounded-lg border border-[#9ca3af] px-2 py-1 text-white text-sm font-semibold">{row.outcome}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
